import { useEffect, useState, useMemo } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { StatusBadge, fmtMoney, EmptyState, SkeletonRow, corporateInput } from "@/components/Shared";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, MagnifyingGlass, PencilSimple, Trash, Users, ArrowsLeftRight, PlugsConnected, DownloadSimple, UploadSimple, CloudArrowDown, CaretRight, Key, Eye, EyeSlash, FloppyDisk, Copy, CheckCircle, XCircle, Warning, Clock, SortAscending, Lightning, LinkSimple } from "@phosphor-icons/react";
import RowMenu from "@/components/RowMenu";
import ClientProfile360 from "@/components/ClientProfile360";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import useSmartPoll from "@/lib/useSmartPoll";

const blank = { name: "", email: "", phone: "", address: "", monthly_fee: 0, status: "active", notes: "" };

export default function Clients() {
  const { t } = useI18n();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc | name_desc | fee_desc | fee_asc | recent
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Transfer dialog
  const [tOpen, setTOpen] = useState(false);
  const [tClient, setTClient] = useState(null);
  const [tForm, setTForm] = useState({ new_address_line: "", new_city: "", new_state: "Texas", new_postcode: "", plan_id: "", desired_start_date: "", notes: "" });
  const [tPlans, setTPlans] = useState([]);
  const [tSaving, setTSaving] = useState(false);

  // Disconnect dialog
  const [dOpen, setDOpen] = useState(false);
  const [dClient, setDClient] = useState(null);
  const [dForm, setDForm] = useState({ reason_code: "non_payment", reason_note: "" });
  const [dReasons, setDReasons] = useState([]);
  const [dSaving, setDSaving] = useState(false);

  // Cancel dialog (final termination)
  const [cOpen, setCOpen] = useState(false);
  const [cClient, setCClient] = useState(null);
  const [cForm, setCForm] = useState({ reason_code: "customer_request", reason_note: "" });
  const [cSaving, setCSaving] = useState(false);

  // Outage / service interruption dialog
  const [oOpen, setOOpen] = useState(false);
  const [oClient, setOClient] = useState(null);
  const [oForm, setOForm] = useState({ has_outage: true, outage_note: "" });
  const [oSaving, setOSaving] = useState(false);

  // Profile 360° drawer
  const [profileClientId, setProfileClientId] = useState(null);
  const [profileStartEdit, setProfileStartEdit] = useState(false);

  // Risk score (live as user types in the New Client form)
  const [risk, setRisk] = useState(null);
  useEffect(() => {
    if (!open || editing) { setRisk(null); return; }
    const fields = { address: form.address, email: form.email, phone: form.phone };
    if (!fields.address && !fields.email && !fields.phone) { setRisk(null); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await api.get("/risk-score", { params: fields });
        setRisk(data);
      } catch (err) { console.warn("[risk-score] failed:", err?.message); }
    }, 600);
    return () => clearTimeout(handle);
  }, [open, editing, form.address, form.email, form.phone]);

  const openDisconnect = async (c) => {
    setDClient(c);
    setDForm({ reason_code: "non_payment", reason_note: "" });
    try {
      const { data } = await api.get("/clients/meta/disconnect-reasons");
      setDReasons(data);
    } catch (_) {
      setDReasons([
        { code: "non_payment", label: "Falta de pago" },
        { code: "relocation", label: "Traslado del cliente" },
        { code: "customer_request", label: "Cliente solicitó cancelar" },
        { code: "other", label: "Otro" },
      ]);
    }
    setDOpen(true);
  };

  const submitDisconnect = async () => {
    setDSaving(true);
    try {
      await api.post(`/clients/${dClient.id}/status`, {
        status: "disconnected",
        reason_code: dForm.reason_code,
        reason_note: dForm.reason_note || null,
      });
      toast.success(t("disconnect.success"));
      setDOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDSaving(false);
    }
  };

  const openCancel = (c) => {
    setCClient(c);
    setCForm({ reason_code: "customer_request", reason_note: "" });
    setCOpen(true);
  };

  const submitCancel = async () => {
    setCSaving(true);
    try {
      await api.post(`/clients/${cClient.id}/status`, {
        status: "cancelled",
        reason_code: cForm.reason_code,
        reason_note: cForm.reason_note || null,
      });
      toast.success("Cliente cancelado. Se sincronizó con Service.");
      setCOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCSaving(false);
    }
  };

  const openOutage = (c) => {
    setOClient(c);
    setOForm({ has_outage: !c.has_outage, outage_note: c.outage_note || "" });
    setOOpen(true);
  };

  const submitOutage = async () => {
    setOSaving(true);
    try {
      await api.post(`/clients/${oClient.id}/outage`, {
        has_outage: oForm.has_outage,
        outage_note: oForm.outage_note || null,
      });
      toast.success(oForm.has_outage
        ? "Cliente marcado en interrupción. Los agentes lo verán al instante."
        : "Interrupción cerrada. Cliente restablecido.");
      setOOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOSaving(false);
    }
  };

  // Detect TDU (transmission/distribution utility) from Texas zip codes
  // so admin can jump straight to the correct outage-map site. When the
  // client has no address, still return the two generic sources (TNMP + ERCOT).
  const detectTdu = (address) => {
    const zipMatch = address ? String(address).match(/\b(\d{5})(?:-\d{4})?\b/) : null;
    const zip = zipMatch ? parseInt(zipMatch[1], 10) : 0;
    const tdus = [];
    // CenterPoint Energy — Houston metro (770xx-775xx, some 776xx-778xx)
    if ((zip >= 77000 && zip <= 77599) || (zip >= 77600 && zip <= 77899)) {
      tdus.push({ name: "CenterPoint Energy", url: "https://tracker.centerpointenergy.com/outagetracker/", region: "Houston metro" });
    }
    // Oncor Electric — Dallas/Fort Worth + North & West Texas
    if ((zip >= 75000 && zip <= 76299) || (zip >= 76500 && zip <= 76999) || (zip >= 79000 && zip <= 79999)) {
      tdus.push({ name: "Oncor Electric", url: "https://stormcenter.oncor.com/", region: "DFW / N & W Texas" });
    }
    // AEP Texas — Central & South Texas
    if ((zip >= 78000 && zip <= 78599)) {
      tdus.push({ name: "AEP Texas", url: "https://outagemap.aeptexas.com/", region: "Central / South Texas" });
    }
    // If no zip-matched TDU, offer the four majors so the admin can still verify.
    if (tdus.length === 0) {
      tdus.push(
        { name: "CenterPoint Energy", url: "https://tracker.centerpointenergy.com/outagetracker/", region: "Houston metro" },
        { name: "Oncor Electric", url: "https://stormcenter.oncor.com/", region: "DFW / N & W Texas" },
        { name: "AEP Texas", url: "https://outagemap.aeptexas.com/", region: "Central / South Texas" },
      );
    }
    // Always append generic sources
    tdus.push(
      { name: "TNMP", url: "https://tnmp.com/outages/outage-map", region: "Texas pockets" },
      { name: "ERCOT (grid)", url: "https://www.ercot.com/gridmktinfo/dashboards/gridconditions", region: "State-wide grid" },
    );
    // Dedup by name
    const seen = new Set();
    return tdus.filter(t => (seen.has(t.name) ? false : (seen.add(t.name), true)));
  };

  const reactivateClient = async (c) => {
    if (!confirm(t("reconnect.confirm"))) return;
    try {
      await api.post(`/clients/${c.id}/status`, { status: "active" });
      toast.success(t("reconnect.success"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openTransfer = async (c) => {
    setTClient(c);
    setTForm({ new_address_line: "", new_city: "", new_state: "Texas", new_postcode: "", plan_id: "", desired_start_date: "", notes: "" });
    try { const { data } = await api.get("/orders/plans-public"); setTPlans(data); } catch (_) { setTPlans([]); }
    setTOpen(true);
  };
  const submitTransfer = async () => {
    setTSaving(true);
    try {
      const payload = { client_id: tClient.id, ...tForm };
      if (!payload.plan_id) delete payload.plan_id;
      if (!payload.desired_start_date) delete payload.desired_start_date;
      if (!payload.notes) delete payload.notes;
      if (!payload.new_postcode) delete payload.new_postcode;

      // Pre-flight: active duplicates + historical matches (excluding self)
      const fullAddr = `${tForm.new_address_line}, ${tForm.new_city}, ${tForm.new_state}`;
      const { data: chk } = await api.get("/clients/check-address", {
        params: { address: fullAddr, exclude_client_id: tClient.id },
      });
      let url = "/orders/transfer-request";
      if (chk.duplicate || (chk.historical_matches?.length || 0) > 0) {
        const parts = [];
        if (chk.duplicate) {
          parts.push(`Aviso: dirección activa para "${chk.existing_client_name}".`);
        }
        if (chk.historical_matches?.length) {
          parts.push("Historial de la dirección:");
          for (const m of chk.historical_matches.slice(0, 5)) {
            const when = m.disconnected_at
              ? new Date(m.disconnected_at).toLocaleDateString()
              : m.transfer_at
                ? `traslado ${new Date(m.transfer_at).toLocaleDateString()}`
                : "—";
            const reason = m.disconnection_reason || (m.status === "transferred" ? "trasladado" : m.status);
            parts.push(`  • ${m.client_name} · ${reason} · ${when}`);
          }
        }
        parts.push("\n¿Continuar con el traslado?");
        const ok = confirm(parts.join("\n"));
        if (!ok) { setTSaving(false); return; }
        if (chk.duplicate) url = "/orders/transfer-request?force=true";
      }
      const { data } = await api.post(url, payload);
      toast.success(`Solicitud de traslado #${data.order_number} enviada a Service`);
      setTOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === "object" && detail?.code === "duplicate_address") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(detail));
      }
    }
    finally { setTSaving(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      // This page only shows active clients — other statuses have their own
      // dedicated sidebar entries (Disconnected clients, Pending reconnection).
      const params = { status: "active" };
      if (search) params.search = search;
      const { data } = await api.get("/clients", { params });
      setClients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  // Mutual sync: poll every 60s (smart — pauses when tab hidden) so changes
  // Support makes (e.g. reactivating a client) appear here without manual refresh.
  useSmartPoll(load, 60000);

  // Active-portfolio stats (this page only lists active clients)
  const stats = useMemo(() => {
    const s = { total: clients.length, mrr: 0, critical: 0, withReferral: 0, outages: 0 };
    for (const c of clients) {
      s.mrr += Number(c.monthly_fee) || 0;
      if (c.payment_alert_level === "critical") s.critical += 1;
      if (c.referral_code) s.withReferral += 1;
      if (c.has_outage) s.outages += 1;
    }
    return s;
  }, [clients]);

  // Search-filtered + sorted list
  const visibleClients = useMemo(() => {
    const sorted = [...clients].sort((a, b) => {
      switch (sortBy) {
        case "name_desc": return (b.name || "").localeCompare(a.name || "");
        case "fee_desc":  return (Number(b.monthly_fee) || 0) - (Number(a.monthly_fee) || 0);
        case "fee_asc":   return (Number(a.monthly_fee) || 0) - (Number(b.monthly_fee) || 0);
        case "recent":    return (b.created_at || "").localeCompare(a.created_at || "");
        case "name_asc":
        default:          return (a.name || "").localeCompare(b.name || "");
      }
    });
    return sorted;
  }, [clients, sortBy]);

  const initials = (name) => {
    if (!name) return "··";
    const parts = name.trim().split(/[\s@.]+/).filter(Boolean);
    if (parts.length === 0) return "··";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c.id);
    setForm({
      name: c.name || "", email: c.email || "", phone: c.phone || "",
      address: c.address || "", monthly_fee: c.monthly_fee || 0,
      status: c.status || "active", notes: c.notes || "",
    });
    setOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, monthly_fee: Number(form.monthly_fee) || 0 };
      if (!payload.email) delete payload.email;
      if (editing) {
        await api.put(`/clients/${editing}`, payload);
        toast.success("Cliente actualizado");
      } else {
        // Pre-flight: detect active duplicates AND historical (disconnected/transferred) matches
        if (payload.address) {
          const { data: chk } = await api.get("/clients/check-address", { params: { address: payload.address } });

          // Build a single confirmation prompt that surfaces BOTH active and historical matches.
          // Active match → blocking-ish (allows force). Historical → informational.
          if (chk.duplicate || (chk.historical_matches?.length || 0) > 0) {
            const parts = [];
            if (chk.duplicate) {
              parts.push(`Aviso: dirección activa para "${chk.existing_client_name}".`);
            }
            if (chk.historical_matches?.length) {
              parts.push("Esta dirección tiene historial:");
              for (const m of chk.historical_matches.slice(0, 5)) {
                const when = m.disconnected_at
                  ? new Date(m.disconnected_at).toLocaleDateString()
                  : m.transfer_at
                    ? `traslado ${new Date(m.transfer_at).toLocaleDateString()}`
                    : "—";
                const reason = m.disconnection_reason || (m.status === "transferred" ? "trasladado" : m.status);
                parts.push(`  • ${m.client_name} · ${reason} · ${when}`);
              }
            }
            parts.push("\n¿Continuar con la creación?");
            const ok = confirm(parts.join("\n"));
            if (!ok) { setSaving(false); return; }
            await api.post(chk.duplicate ? "/clients?force=true" : "/clients", payload);
            toast.success(chk.duplicate ? "Cliente creado (duplicado forzado)" : "Cliente creado");
          } else {
            await api.post("/clients", payload);
            toast.success("Cliente creado");
          }
        } else {
          await api.post("/clients", payload);
          toast.success("Cliente creado");
        }
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Backend may also reject (e.g., race condition). Surface duplicate code as toast.
      if (typeof detail === "object" && detail?.code === "duplicate_address") {
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(detail) || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Eliminar este cliente? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <PageShell
      section="Operación"
      page="Clientes activos"
      context={`${clients.length} ${clients.length === 1 ? "activo" : "activos"}`}
      testid="clients-toolbar"
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`${API}/csv/clients/export`}
            data-testid="export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white rounded transition-colors h-9"
            title="Exportar CSV"
          >
            <DownloadSimple size={14} /> CSV
          </a>
          <input
            ref={(r) => (window.__csv_clients_input = r)}
            type="file"
            accept=".csv"
            hidden
            data-testid="import-csv-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const fd = new FormData();
              fd.append("file", file);
              try {
                const { data } = await api.post("/csv/clients/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(`${data.created} creado(s), ${data.skipped || 0} omitido(s)`);
                if (data.errors?.length) toast.error(`${data.errors.length} errores — primeros: ${data.errors.slice(0, 2).join(" · ")}`);
                load();
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail));
              } finally {
                e.target.value = "";
              }
            }}
          />
          <button
            onClick={() => window.__csv_clients_input?.click()}
            data-testid="import-csv-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:border-slate-400 hover:text-slate-900 dark:hover:text-white rounded transition-colors h-9"
          >
            <UploadSimple size={14} /> Importar
          </button>
          <Button onClick={openNew} data-testid="add-client-button" className="bg-slate-900 hover:bg-slate-800 text-white h-9">
            <Plus size={15} className="mr-1.5" /> Nuevo cliente
          </Button>
        </div>
      }
    >

      <div className="space-y-5 mb-5">
        {/* ── Active portfolio summary strip ── */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
            <div className="px-5 py-4 relative">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-emerald-500" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 font-semibold flex items-center gap-1.5">
                <CheckCircle size={11} weight="fill" /> Clientes activos
              </div>
              <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900">{stats.total}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">en el portafolio</div>
            </div>
            <div className="px-5 py-4 relative">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-amber-500" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-amber-700 font-semibold">MRR activo</div>
              <div className="mt-1.5 text-2xl font-semibold font-mono tabular-nums text-slate-900">{fmtMoney(stats.mrr)}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">ingreso recurrente mensual</div>
            </div>
            <div className="px-5 py-4 relative">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-rose-500" />
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-1.5">
                <Warning size={11} weight="fill" className="text-rose-500" /> Mora crítica
              </div>
              <div className={`mt-1.5 text-2xl font-semibold font-mono tabular-nums ${stats.critical > 0 ? "text-rose-700" : "text-slate-900"}`}>{stats.critical}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">{stats.critical > 0 ? "requieren atención" : "todos al día"}</div>
            </div>
            <div className="px-5 py-4 relative">
              {stats.outages > 0 && <div className="absolute top-0 left-0 right-0 h-[2px] bg-orange-500" />}
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-1.5">
                <Lightning size={11} weight="fill" className={stats.outages > 0 ? "text-orange-500" : "text-slate-400"} /> Interrupciones
              </div>
              <div className={`mt-1.5 text-2xl font-semibold font-mono tabular-nums ${stats.outages > 0 ? "text-orange-700" : "text-slate-900"}`}>{stats.outages}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {stats.outages > 0
                  ? "sin luz · notificar clientes"
                  : `sin cortes · ${stats.withReferral} con referido`}
              </div>
            </div>
          </div>
        </div>

        {/* ── Toolbar: search + sort ── */}
        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nombre, email o dirección..."
                data-testid="clients-search-input"
                className="pl-9 h-10 border-slate-200"
              />
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <SortAscending size={14} className="text-slate-400" />
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-10 border-slate-200 text-xs w-[180px]" data-testid="clients-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Nombre · A–Z</SelectItem>
                  <SelectItem value="name_desc">Nombre · Z–A</SelectItem>
                  <SelectItem value="fee_desc">Tarifa · mayor</SelectItem>
                  <SelectItem value="fee_asc">Tarifa · menor</SelectItem>
                  <SelectItem value="recent">Más recientes</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[11px] text-slate-500 font-mono whitespace-nowrap ml-1" data-testid="clients-count-hint">
                {visibleClients.length}/{stats.total}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 w-[22%]">Cliente</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 w-[14%]">Contacto</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Dirección de servicio</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 text-right w-[9%]">Tarifa</th>
              <th className="px-5 py-3 text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 w-[14%]">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} cols={5} />)}
            {!loading && clients.length === 0 && (
              <tr><td colSpan={5}>
                <EmptyState
                  icon={Users}
                  title="Aún no hay clientes"
                  message="Comienza agregando tu primer cliente para emitir facturas y rastrear pagos."
                  action={
                    <Button onClick={openNew} data-testid="empty-add-client" className="bg-slate-900 hover:bg-slate-800 text-white h-9">
                      <Plus size={15} className="mr-1.5" /> Nuevo cliente
                    </Button>
                  }
                />
              </td></tr>
            )}
            {!loading && clients.length > 0 && visibleClients.length === 0 && (
              <tr><td colSpan={5}>
                <EmptyState
                  icon={MagnifyingGlass}
                  title="Sin resultados"
                  message="Ajusta la búsqueda para ver clientes activos."
                  action={
                    <Button onClick={() => setSearch("")} variant="outline" className="h-9" data-testid="clear-filters">
                      Limpiar búsqueda
                    </Button>
                  }
                />
              </td></tr>
            )}
            {visibleClients.map((c) => {
              const critical = c.payment_alert_level === "critical";
              const isActiveClient = c.status === "active";
              const isExpanded = false;
              // Avatar tone: active=emerald, pending=amber, cancelled/disc=slate/rose
              const avatarTone = c.status === "active"
                ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                : c.status === "pending" || c.status === "pending_reconnection"
                  ? "bg-amber-50 text-amber-700 ring-amber-200"
                  : c.status === "cancelled" || c.status === "disconnected"
                    ? "bg-rose-50 text-rose-700 ring-rose-200"
                    : "bg-slate-100 text-slate-600 ring-slate-200";
              return (
              <>
              <tr
                key={c.id}
                data-testid={`client-row-${c.id}`}
                className={critical ? "bg-red-50/40 hover:bg-red-50/70" : "hover:bg-slate-50 transition-colors"}
              >
                <td className="px-5 py-3.5 font-medium text-slate-900">
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-full ring-1 font-semibold text-[11px] font-mono ${avatarTone}`}>
                      {initials(c.name)}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        {critical && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" title="Mora crítica" />}
                        {c.has_outage && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider bg-orange-100 text-orange-800 border border-orange-300 shrink-0 animate-pulse"
                            title={c.outage_note ? `Interrupción: ${c.outage_note}` : "Cliente en interrupción de servicio"}
                            data-testid={`outage-flag-${c.id}`}
                          >
                            <Lightning size={9} weight="fill" /> Sin luz
                          </span>
                        )}
                        <button
                          onClick={() => { setProfileStartEdit(false); setProfileClientId(c.id); }}
                          data-testid={`open-profile-${c.id}`}
                          className="truncate text-left hover:underline hover:text-slate-950 dark:hover:text-white transition-colors"
                          title="Abrir perfil 360°"
                        >
                          {c.name}
                        </button>
                      </div>
                      {c.referral_code && (
                        <button
                          onClick={() => { navigator.clipboard.writeText(c.referral_code); toast.success(`Código ${c.referral_code} copiado`); }}
                          className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                          title="Copiar código de referido"
                          data-testid={`copy-ref-${c.id}`}
                        >
                          {c.referral_code}
                        </button>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-slate-600 align-top">
                  {c.email || c.phone ? (
                    <>
                      {c.email && <div className="truncate text-[13px]" title={c.email}>{c.email}</div>}
                      {c.phone && <div className="text-[11px] text-slate-500 font-mono mt-0.5">{c.phone}</div>}
                    </>
                  ) : (
                    <span className="text-slate-300 text-xs italic">Sin datos</span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-slate-700 align-top">
                  <div className="text-[13px] leading-snug break-words" title={c.address || ""}>
                    {c.address || "—"}
                  </div>
                  {(c.city || c.zip_code) && (
                    <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                      {[c.city, c.state, c.zip_code].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </td>
                <td className="px-5 py-3.5 text-right font-mono tabular-nums text-slate-900 align-top">{fmtMoney(c.monthly_fee)}</td>
                <td className="px-5 py-3.5 align-top">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid={`status-menu-${c.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-1"
                        title="Cambiar estado o solicitar acción"
                      >
                        {critical ? <StatusBadge status="critical" /> : <StatusBadge status={c.status} />}
                        <CaretRight size={11} weight="bold" className="text-slate-400 rotate-90" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56">
                      <DropdownMenuItem
                        onClick={() => { setProfileStartEdit(true); setProfileClientId(c.id); }}
                        data-testid={`edit-client-${c.id}`}
                        className="text-xs cursor-pointer"
                      >
                        <PencilSimple size={13} className="mr-2 text-slate-500" />
                        Editar cliente
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openOutage(c)}
                        data-testid={`outage-client-${c.id}`}
                        className={`text-xs cursor-pointer ${c.has_outage ? "text-emerald-700" : "text-orange-700"}`}
                      >
                        <Lightning size={13} className="mr-2" weight={c.has_outage ? "regular" : "fill"} />
                        {c.has_outage ? "Cerrar interrupción" : "Verificar / marcar interrupción"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {c.status === "disconnected" ? (
                        <DropdownMenuItem
                          onClick={() => reactivateClient(c)}
                          data-testid={`reactivate-client-${c.id}`}
                          className="text-xs cursor-pointer text-emerald-700"
                        >
                          <PlugsConnected size={13} className="mr-2" />
                          Reconectar (marcar activo)
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => openDisconnect(c)}
                          data-testid={`disconnect-client-${c.id}`}
                          className="text-xs cursor-pointer text-amber-700"
                        >
                          <PlugsConnected size={13} className="mr-2" />
                          Marcar como desconectado
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => openTransfer(c)}
                        data-testid={`transfer-client-${c.id}`}
                        className="text-xs cursor-pointer"
                      >
                        <ArrowsLeftRight size={13} className="mr-2 text-slate-500" />
                        Solicitar traslado
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => openCancel(c)}
                        data-testid={`cancel-client-${c.id}`}
                        className="text-xs cursor-pointer text-rose-700"
                      >
                        <XCircle size={13} className="mr-2" />
                        Solicitar cancelación
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleDelete(c.id)}
                        data-testid={`delete-client-${c.id}`}
                        className="text-xs cursor-pointer text-rose-700"
                      >
                        <Trash size={13} className="mr-2" />
                        Eliminar registro
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
              </>
            );})}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"
          data-testid="client-form-dialog"
        >
          {/* Header — sober corporate strip */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                {editing ? "Editar" : "Nuevo"} · Cliente
              </div>
              {editing && form.name && (
                <>
                  <div className="h-3 w-px bg-slate-300 dark:bg-slate-700" />
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[280px]">{form.name}</div>
                </>
              )}
            </div>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                form.status === "active"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/30 dark:text-emerald-400"
                  : "border-slate-200 bg-slate-100 text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${form.status === "active" ? "bg-emerald-500" : "bg-slate-400"}`} />
              {form.status === "active" ? "Activo" : "Inactivo"}
            </span>
          </div>

          {/* Risk banner (only for new clients) */}
          {!editing && risk && risk.tier !== "LOW" && (
            <div
              className={`px-5 py-2 border-b text-[11px] flex items-start justify-between gap-3 ${
                risk.tier === "HIGH"
                  ? "border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20 text-red-700 dark:text-red-300"
                  : "border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-300"
              }`}
              data-testid="risk-banner"
            >
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold">
                  Riesgo {risk.tier === "HIGH" ? "alto" : "medio"} · Score {risk.score}
                </div>
                <div className="mt-0.5 text-[11px] leading-snug truncate">
                  {risk.signals.slice(0, 3).map((s) => s.label).join(" · ")}
                </div>
              </div>
            </div>
          )}

          {/* Body — two-column dense form */}
          <div className="px-5 py-4">
            {/* Section: Identity */}
            <div className="grid grid-cols-12 gap-x-4 gap-y-3">
              <div className="col-span-12 sm:col-span-7 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Nombre completo *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="client-name-input"
                  className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </div>
              <div className="col-span-12 sm:col-span-5 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Estado</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="client-status-select" className="h-9 text-sm border-slate-200 dark:border-slate-800"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activo</SelectItem>
                    <SelectItem value="inactive">Inactivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-12 sm:col-span-7 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="client-email-input"
                  className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </div>
              <div className="col-span-12 sm:col-span-5 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Teléfono</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="client-phone-input"
                  className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </div>

              {/* Divider */}
              <div className="col-span-12 flex items-center gap-2 pt-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 dark:text-slate-500 font-semibold">Servicio</div>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
              </div>

              <div className="col-span-12 sm:col-span-8 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Dirección de servicio</Label>
                <Input
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  data-testid="client-address-input"
                  className="h-9 text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </div>
              <div className="col-span-12 sm:col-span-4 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Tarifa mensual (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.monthly_fee}
                  onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })}
                  data-testid="client-fee-input"
                  className="h-9 text-sm font-mono tabular-nums border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                />
              </div>

              <div className="col-span-12 space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Notas internas</Label>
                <Textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="client-notes-input"
                  className="text-sm border-slate-200 dark:border-slate-800 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none"
                />
              </div>
            </div>
          </div>

          {/* Footer — sober strip */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
              {editing ? "Los cambios se aplican al guardar" : "Se enviará al pipeline de activación"}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="h-8 text-xs border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !form.name}
                data-testid="save-client-button"
                className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
              >
                {saving ? "Guardando…" : editing ? "Guardar cambios" : "Crear cliente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Request dialog */}
      <Dialog open={tOpen} onOpenChange={setTOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Solicitar traslado</DialogTitle>
          </DialogHeader>
          {tClient && (
            <div className="bg-slate-50 border border-slate-200 px-3 py-2 mb-2 text-xs">
              <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Cliente</div>
              <div className="font-semibold text-slate-900 mt-0.5">{tClient.name}</div>
              <div className="text-slate-500 mt-1">Dirección actual: {tClient.address || "—"}</div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nueva dirección *</Label>
              <Input value={tForm.new_address_line} onChange={(e) => setTForm({...tForm, new_address_line: e.target.value})} className={corporateInput} data-testid="t-addr" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Ciudad *</Label>
              <Input value={tForm.new_city} onChange={(e) => setTForm({...tForm, new_city: e.target.value})} className={corporateInput} data-testid="t-city" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Código postal</Label>
              <Input value={tForm.new_postcode} onChange={(e) => setTForm({...tForm, new_postcode: e.target.value})} className={corporateInput} data-testid="t-zip" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Plan (opcional)</Label>
              <select value={tForm.plan_id} onChange={(e) => setTForm({...tForm, plan_id: e.target.value})} className={corporateInput} data-testid="t-plan">
                <option value="">Mantener actual</option>
                {tPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Fecha deseada</Label>
              <Input type="date" value={tForm.desired_start_date} onChange={(e) => setTForm({...tForm, desired_start_date: e.target.value})} className={corporateInput} />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Notas para Service</Label>
              <Textarea rows={2} value={tForm.notes} onChange={(e) => setTForm({...tForm, notes: e.target.value})} className="border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" data-testid="t-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTOpen(false)} disabled={tSaving}>Cancelar</Button>
            <Button onClick={submitTransfer} disabled={tSaving || !tForm.new_address_line || !tForm.new_city} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="t-submit">
              {tSaving ? "Enviando..." : "Enviar a Service"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Disconnect Request dialog — compact corporate style */}
      <Dialog open={dOpen} onOpenChange={setDOpen}>
        <DialogContent
          className="max-w-sm p-0 gap-0 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950"
          data-testid="disconnect-dialog"
        >
          {/* Header strip */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">
                {t("disconnect.title")}
              </div>
              {dClient?.name && (
                <>
                  <div className="h-3 w-px bg-slate-300 dark:bg-slate-700" />
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate max-w-[180px]">{dClient.name}</div>
                </>
              )}
            </div>
          </div>

          {/* Body */}
          {dClient && (
            <div className="px-4 py-3 space-y-3">
              <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate" title={dClient.address}>
                {dClient.address || "—"}
              </div>

              <div className="space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  {t("disconnect.reason")} *
                </Label>
                <select
                  value={dForm.reason_code}
                  onChange={(e) => setDForm({ ...dForm, reason_code: e.target.value })}
                  data-testid="d-reason"
                  className="w-full h-9 text-sm px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
                >
                  {dReasons.map((r) => (
                    <option key={r.code} value={r.code}>{r.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  {t("disconnect.note")}
                </Label>
                <Textarea
                  rows={2}
                  value={dForm.reason_note}
                  onChange={(e) => setDForm({ ...dForm, reason_note: e.target.value })}
                  placeholder="Cliente informa que no tiene energía"
                  className="text-sm border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 resize-none"
                  data-testid="d-note"
                />
              </div>
            </div>
          )}

          {/* Footer strip */}
          <div className="flex items-center justify-end gap-2 px-4 py-2.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
            <Button
              variant="outline"
              onClick={() => setDOpen(false)}
              disabled={dSaving}
              className="h-8 text-xs border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={submitDisconnect}
              disabled={dSaving || !dForm.reason_code}
              className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="d-submit"
            >
              {dSaving ? t("common.saving") : t("disconnect.submit")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel client dialog */}
      <Dialog open={cOpen} onOpenChange={setCOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <XCircle size={16} weight="fill" className="text-rose-500" />
              Solicitar cancelación
            </DialogTitle>
          </DialogHeader>
          {cClient && (
            <div className="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/60 px-3 py-2 mb-2 text-xs rounded">
              <div className="text-rose-700 dark:text-rose-400 uppercase tracking-wider text-[10px] font-semibold">Cliente a cancelar</div>
              <div className="font-semibold text-slate-900 dark:text-slate-100 mt-0.5">{cClient.name}</div>
              <div className="text-slate-600 dark:text-slate-400 mt-1">{cClient.address || "—"}</div>
              <div className="text-[10px] text-rose-600 dark:text-rose-400 mt-2 leading-relaxed">
                Aviso — esta acción termina el servicio del cliente. Se sincroniza con Service inmediatamente.
              </div>
            </div>
          )}
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Motivo de cancelación *</Label>
              <select
                value={cForm.reason_code}
                onChange={(e) => setCForm({ ...cForm, reason_code: e.target.value })}
                data-testid="c-reason"
                className="w-full h-11 text-sm px-3 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 focus:outline-none"
              >
                <option value="customer_request">Cliente solicitó cancelar</option>
                <option value="non_payment">Falta de pago</option>
                <option value="relocation">Traslado del cliente</option>
                <option value="fraud">Fraude / verificación fallida</option>
                <option value="other">Otro</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Nota (opcional)</Label>
              <Textarea
                rows={2}
                value={cForm.reason_note}
                onChange={(e) => setCForm({ ...cForm, reason_note: e.target.value })}
                placeholder="Detalles adicionales para Service…"
                className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                data-testid="c-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCOpen(false)} disabled={cSaving}>Cancelar</Button>
            <Button
              onClick={submitCancel}
              disabled={cSaving || !cForm.reason_code}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="c-submit"
            >
              {cSaving ? "Guardando…" : "Confirmar cancelación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outage / service interruption dialog */}
      <Dialog open={oOpen} onOpenChange={setOOpen}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800" data-testid="outage-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
              <Lightning size={16} weight="fill" className="text-orange-500" />
              {oClient?.has_outage ? "Estado de interrupción" : "Verificar interrupción de servicio"}
            </DialogTitle>
          </DialogHeader>
          {oClient && (
            <>
              <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 px-3 py-2.5 mb-2 text-xs rounded">
                <div className="text-slate-500 dark:text-slate-400 uppercase tracking-wider text-[10px] font-semibold">Cliente</div>
                <div className="font-semibold text-slate-900 dark:text-slate-100 mt-0.5">{oClient.name}</div>
                <div className="text-slate-600 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                  <span>{oClient.address || "—"}</span>
                </div>
                {oClient.phone && (
                  <div className="text-slate-500 dark:text-slate-400 mt-1 font-mono text-[11px]">Tel · {oClient.phone}</div>
                )}
              </div>

              {/* Outage-map quick links */}
              <div className="mb-3">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500 dark:text-slate-400 mb-1.5">
                  Consultar en mapa TDU
                </div>
                <div className="grid grid-cols-1 gap-1.5" data-testid="tdu-links">
                  {detectTdu(oClient.address).map((t) => (
                    <a
                      key={t.name}
                      href={t.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-2 px-3 py-2 border border-slate-200 dark:border-slate-800 rounded hover:bg-slate-50 dark:hover:bg-slate-900 hover:border-slate-400 text-xs group transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <LinkSimple size={12} className="text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300" />
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{t.name}</span>
                        <span className="text-[10px] text-slate-500 dark:text-slate-500">{t.region}</span>
                      </span>
                      <CaretRight size={11} className="text-slate-400 group-hover:text-slate-700 dark:group-hover:text-slate-300" />
                    </a>
                  ))}
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">
                  Consulta el mapa de la TDU (transmisión/distribución) que atiende la dirección del cliente para confirmar si hay corte activo.
                </p>
              </div>

              {/* Toggle + note */}
              <div className="space-y-3 border-t border-slate-100 dark:border-slate-800 pt-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={oForm.has_outage}
                    onChange={(e) => setOForm({ ...oForm, has_outage: e.target.checked })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 dark:border-slate-700 text-orange-500 focus:ring-orange-400"
                    data-testid="o-toggle"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-slate-900 dark:text-slate-100">Marcar cliente en interrupción</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Los agentes verán una alerta y podrán contactar al cliente proactivamente.</div>
                  </div>
                </label>
                {oForm.has_outage && (
                  <div className="space-y-1.5">
                    <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                      Notas de la interrupción
                    </Label>
                    <Textarea
                      rows={2}
                      value={oForm.outage_note}
                      onChange={(e) => setOForm({ ...oForm, outage_note: e.target.value })}
                      placeholder="Corte confirmado · ETA 6:00pm"
                      className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 focus:border-slate-900 focus:ring-1 focus:ring-slate-900"
                      data-testid="o-note"
                    />
                  </div>
                )}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOOpen(false)} disabled={oSaving}>Cerrar</Button>
            <Button
              onClick={submitOutage}
              disabled={oSaving}
              className={oForm.has_outage ? "bg-orange-600 hover:bg-orange-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
              data-testid="o-submit"
            >
              {oSaving ? "Guardando…" : oForm.has_outage ? "Marcar en interrupción" : "Cerrar interrupción"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Profile 360° drawer */}
      <ClientProfile360
        clientId={profileClientId}
        open={!!profileClientId}
        startInEdit={profileStartEdit}
        onOpenChange={(v) => { if (!v) { setProfileClientId(null); setProfileStartEdit(false); } }}
        onSaved={() => { setProfileStartEdit(false); load(); }}
      />
    </PageShell>
  );
}

function ServiceCredentialsRow({ client, onSaved }) {
  const [form, setForm] = useState({
    portal_username: client.portal_username || "",
    portal_password: client.portal_password || "",
    account_number: client.account_number || "",
    service_start_date: client.service_start_date || "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm({
      portal_username: client.portal_username || "",
      portal_password: client.portal_password || "",
      account_number: client.account_number || "",
      service_start_date: client.service_start_date || "",
    });
    setDirty(false);
  }, [client.id, client.portal_username, client.portal_password, client.account_number, client.service_start_date]);

  const update = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      // Preserve all existing client fields, add the credentials
      await api.put(`/clients/${client.id}`, { ...client, ...form });
      toast.success("Credenciales guardadas");
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error");
    } finally { setBusy(false); }
  };

  const copy = (val, label) => {
    if (!val) return;
    navigator.clipboard.writeText(val);
    toast.success(`${label} copiado`);
  };

  return (
    <tr className="bg-slate-50/60 dark:bg-slate-900/40 border-t border-b border-slate-200 dark:border-slate-800">
      <td colSpan={6} className="px-8 py-4">
        <div className="flex items-center gap-2 mb-3">
          <Key size={13} className="text-amber-500" weight="fill" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Credenciales de servicio — {client.name}
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {/* Portal username */}
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Usuario portal</Label>
            <div className="relative">
              <Input
                value={form.portal_username}
                onChange={(e) => update("portal_username", e.target.value)}
                placeholder="ej. juan@ejemplo.com"
                className={`${corporateInput} pr-8`}
                data-testid={`cred-user-${client.id}`}
              />
              {form.portal_username && (
                <button
                  type="button"
                  onClick={() => copy(form.portal_username, "Usuario")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  title="Copiar"
                >
                  <Copy size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Portal password */}
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Contraseña</Label>
            <div className="relative">
              <Input
                type={showPwd ? "text" : "password"}
                value={form.portal_password}
                onChange={(e) => update("portal_password", e.target.value)}
                placeholder="••••••••"
                className={`${corporateInput} pr-14`}
                data-testid={`cred-pwd-${client.id}`}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setShowPwd(v => !v)}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label={showPwd ? "Hide" : "Show"}
                >
                  {showPwd ? <EyeSlash size={12} /> : <Eye size={12} />}
                </button>
                {form.portal_password && (
                  <button
                    type="button"
                    onClick={() => copy(form.portal_password, "Contraseña")}
                    className="text-slate-400 hover:text-slate-700"
                    title="Copiar"
                  >
                    <Copy size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Account number */}
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Número de cuenta</Label>
            <div className="relative">
              <Input
                value={form.account_number}
                onChange={(e) => update("account_number", e.target.value)}
                placeholder="ej. 12345678"
                className={`${corporateInput} font-mono pr-8`}
                data-testid={`cred-acct-${client.id}`}
              />
              {form.account_number && (
                <button
                  type="button"
                  onClick={() => copy(form.account_number, "Cuenta")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  title="Copiar"
                >
                  <Copy size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Service start date */}
          <div>
            <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Fecha de inicio</Label>
            <Input
              type="date"
              value={form.service_start_date}
              onChange={(e) => update("service_start_date", e.target.value)}
              className={corporateInput}
              data-testid={`cred-start-${client.id}`}
            />
          </div>
        </div>

        {dirty && (
          <div className="mt-3 flex justify-end">
            <Button
              onClick={save}
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs"
              data-testid={`cred-save-${client.id}`}
            >
              <FloppyDisk size={12} className="mr-1.5" weight="fill" />
              {busy ? "Guardando..." : "Guardar credenciales"}
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

